/**
 * Generic floating context menu used everywhere a right-click menu is shown.
 *
 *   openContextMenu(e, [
 *     { label: '복제', shortcut: '⌘D', onClick: () => duplicateLayer(id) },
 *     {},                                       // separator
 *     { label: '삭제', shortcut: 'Del', onClick: () => removeLayer(id), danger: true },
 *   ]);
 *
 * Closes on outside-click, scroll, blur, Escape, and after any item is invoked.
 */

export interface MenuItem {
  /** Omit (or pass `{}`) to render a separator. */
  label?: string;
  shortcut?: string;
  disabled?: boolean;
  danger?: boolean;
  onClick?: () => void;
}

let openMenu: HTMLElement | null = null;

export function openContextMenu(e: MouseEvent, items: MenuItem[]): void {
  closeContextMenu();
  // Defensively prevent the browser default
  e.preventDefault();
  e.stopPropagation();

  const menu = document.createElement('div');
  menu.className = 'ctx-menu';
  menu.setAttribute('role', 'menu');

  for (const it of items) {
    if (!it.label) {
      const sep = document.createElement('div');
      sep.className = 'ctx-sep';
      menu.appendChild(sep);
      continue;
    }
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'ctx-item';
    if (it.disabled) row.classList.add('disabled');
    if (it.danger) row.classList.add('danger');

    const label = document.createElement('span');
    label.className = 'ctx-label';
    label.textContent = it.label;
    row.appendChild(label);

    if (it.shortcut) {
      const sc = document.createElement('span');
      sc.className = 'ctx-shortcut';
      sc.textContent = it.shortcut;
      row.appendChild(sc);
    }
    if (!it.disabled && it.onClick) {
      row.addEventListener('click', () => {
        closeContextMenu();
        it.onClick!();
      });
    }
    menu.appendChild(row);
  }

  // Position with viewport clamp
  document.body.appendChild(menu);
  const rect = menu.getBoundingClientRect();
  const x = Math.min(e.clientX, window.innerWidth - rect.width - 4);
  const y = Math.min(e.clientY, window.innerHeight - rect.height - 4);
  menu.style.left = `${Math.max(4, x)}px`;
  menu.style.top = `${Math.max(4, y)}px`;

  openMenu = menu;

  // Close on outside interaction (defer one tick so the right-click itself doesn't close it)
  setTimeout(() => {
    document.addEventListener('mousedown', onOutside, { once: true, capture: true });
    document.addEventListener('keydown', onKey, { once: true });
    window.addEventListener('blur', closeContextMenu, { once: true });
    window.addEventListener('resize', closeContextMenu, { once: true });
  }, 0);
}

export function closeContextMenu(): void {
  if (!openMenu) return;
  openMenu.remove();
  openMenu = null;
}

function onOutside(e: MouseEvent): void {
  if (!openMenu) return;
  if ((e.target as Node) && openMenu.contains(e.target as Node)) {
    // re-arm so future clicks still close
    document.addEventListener('mousedown', onOutside, { once: true, capture: true });
    return;
  }
  closeContextMenu();
}
function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    closeContextMenu();
    return;
  }
  // re-arm
  document.addEventListener('keydown', onKey, { once: true });
}
